import React, { FC }                                                                                                             from 'react';
import * as Yup                                                                                                                  from "yup";
import { Cell, Grid }                                                                                                            from "styled-css-grid";
import { TextField }                                                                                                             from "@src/views/components/forms";
import { BulkUpdateFormControlContainer }                                                                                        from "@src/views/pages/projects/activities/bulk/BulkUpdateFormControlContainer";
import { ProjectActivityBulkUpdateShell, ProjectActivityBulkUpdateShellFormContainerProps, ProjectActivityBulkUpdateShellProps } from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateShell";
import { ProjectActivityBulkUpdateMultiDialogProps }                                                                             from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateMultiDialog";
import { ENTITY_ICONS }                                                                                                          from "@src/constants";
import _                                                                                                                         from "underscore";
import { useFeatureFlags }                                                                                                       from "@src/hooks";


type FormTypeAttributes = {
    ongoingCostPerQuarter: number,
    ongoingCostQuarters?: number,
    ongoingCostNote?: string,
}

const formDefaultValues = {
    ongoingCostPerQuarter: null,
    ongoingCostQuarters:   null,
    ongoingCostNote:       null,
}

const validationSchema = (
    Yup.object().shape( {
        ongoingCostPerQuarter: Yup.mixed()
                                  .when( 'ongoingCostPerQuarter', {
                                      is:        value => !_.isEmpty( value ),
                                      then:      Yup.number().min( 0, 'Must be zero or more.' ).required( 'Please enter a value' ),
                                      otherwise: Yup.number().nullable()
                                  } ),
        ongoingCostQuarters:   Yup.mixed()
                                  .when( 'ongoingCostQuarters', {
                                      is:        value => !_.isEmpty( value ),
                                      then:      Yup.number().min( 0, 'Must be zero or more.' ).required( 'Please enter a value' ),
                                      otherwise: Yup.number().nullable()
                                  } )
    }, [
        [ 'ongoingCostPerQuarter', 'ongoingCostPerQuarter' ],
        [ 'ongoingCostQuarters', 'ongoingCostQuarters' ]
    ] )
)

const mutationAttributeFormatter: ProjectActivityBulkUpdateShellProps<FormTypeAttributes>['prepareFormValuesForMutation'] = ( values, formik ) => {

    const {
              ongoingCostPerQuarter,
              ongoingCostQuarters,
              ongoingCostNote,
          } = values

    const changes: Record<any, any> = {};

    if( _.isNumber( ongoingCostPerQuarter ) ){
        changes.ongoingCostPerQuarter = ongoingCostPerQuarter;
    }

    if( _.isNumber( ongoingCostQuarters ) ){
        changes.ongoingCostQuarters = ongoingCostQuarters;
    }

    if( !_.isEmpty( ongoingCostNote ) ){
        changes.ongoingCostNote = ongoingCostNote;
    }

    return changes;
}

interface StaticProjectActivityBulkUpdateOngoingCostProps extends Pick<ProjectActivityBulkUpdateShellProps<FormTypeAttributes>, 'supportModals' | 'supportToasts'>,
    Pick<ProjectActivityBulkUpdateMultiDialogProps, 'projectId' | 'projectVersionId' | 'projectActivityIds'> {
    formContainer: FC<ProjectActivityBulkUpdateShellFormContainerProps>
}

const StaticProjectActivityBulkUpdateOngoingCostDefaultProps = {
    supportModals: false,
    supportToasts: true,
} satisfies Partial<StaticProjectActivityBulkUpdateOngoingCostProps>

function StaticProjectActivityBulkUpdateOngoingCost( _props: StaticProjectActivityBulkUpdateOngoingCostProps ){

    const props = { ...StaticProjectActivityBulkUpdateOngoingCostDefaultProps, ..._props };

    const featureFlags = useFeatureFlags();

    return (

        <ProjectActivityBulkUpdateShell<FormTypeAttributes>
            projectId={ props.projectId }
            projectVersionId={ props.projectVersionId }
            ids={ props.projectActivityIds }
            supportToasts={ props.supportToasts }
            supportModals={ props.supportModals }
            defaultFormValues={ formDefaultValues }
            validationSchema={ validationSchema }
            formContainer={ props.formContainer }
            prepareFormValuesForMutation={ mutationAttributeFormatter }>

            {/* FORM CONTENT */ }
            { ( { formik, isProcessing, isLoading } ) => {

                return (

                    <BulkUpdateFormControlContainer
                        icon={ ENTITY_ICONS.BUDGET.ONGOING_COSTS }
                        title={ 'Update Ongoing Cost' }
                        description={ `Migrate the ongoing cost for multiple activities to the one specified.` }>

                        <Grid columns={ `50% auto` } rows={ `auto auto` } columnGap={ "30px" } rowGap={ '30px' }>
                            <Cell>

                                <TextField autoFocus={ true }
                                           type="number"
                                           label={ "Ongoing Quarterly Cost" }
                                           descriptionText={ `The cost to maintain these activities, applied each quarter after the initially scheduled one until the ${ featureFlags.useDEMSpecificOngoingCostDuration ? 'specified number of quarters' : 'project ends' }.` }
                                           name={ "ongoingCostPerQuarter" }
                                           min={ 0 }
                                           placeholder="$0.00"
                                           disabled={ isLoading || isProcessing }/>
                            </Cell>

                            { featureFlags.useDEMSpecificOngoingCostDuration && (
                                <Cell>

                                    <TextField
                                        type="number"
                                        label={ "# Quarter(s)" }
                                        name={ "ongoingCostQuarters" }
                                        min={ 1 }
                                        placeholder="1"
                                        disabled={ isLoading || isProcessing }
                                    />

                                </Cell>
                            ) }

                            <Cell>

                                <TextField
                                    type="text"
                                    name={ "ongoingCostNote" }
                                    label={ "Cost Note" }
                                    descriptionText={ `A single description for all selected activity's ongoing costs. This will replace any current notes.` }
                                    disabled={ isLoading || isProcessing }/>

                            </Cell>
                        </Grid>

                    </BulkUpdateFormControlContainer>

                )
            } }

        </ProjectActivityBulkUpdateShell>

    );
}

export {
    StaticProjectActivityBulkUpdateOngoingCost,
    StaticProjectActivityBulkUpdateOngoingCostDefaultProps,
    type StaticProjectActivityBulkUpdateOngoingCostProps,
};
